import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth-context.js';

/** Route guard: only GHL admins may see the wrapped element; others go to the mailbox. */
function RequireAdmin({ children }: { children: React.ReactElement }): React.ReactElement {
  const { isAdmin, loading } = useAuth();
  if (loading) return <></>;
  return isAdmin ? children : <Navigate to="/app/mailbox" replace />;
}

// Staff pages (GHL iframe)
import StaffLayout from './pages/app/StaffLayout.js';
import GHLAuthGate from './components/GHLAuthGate.js';
import MailboxPage from './pages/app/mailbox/MailboxPage.js';
import FormsPage from './pages/app/forms/FormsPage.js';
import SettingsPage from './pages/app/settings/SettingsPage.js';
import ContactWidgetPage from './pages/app/contact-widget/ContactWidgetPage.js';

// Patient portal (magic link)
import ContactPortalPage from './pages/portal/ContactPortalPage.js';

// Public form (open or OTP-gated)
import PublicFormPage from './pages/public-form/PublicFormPage.js';

// Public package (OTP-gated, multi-form run)
import PublicPackagePage from './pages/public-form/PublicPackagePage.js';

/**
 * Top-level router for ClearIntake.
 *
 * Routes:
 *   /app/mailbox          — Staff secure mailbox (GHL iframe, full layout)
 *   /app/forms            — Staff form management (GHL iframe, full layout)
 *   /app/settings         — Tenant settings (GHL iframe, full layout)
 *   /app/contact/:id      — Staff contact widget (GHL iframe, compact — no StaffLayout)
 *   /c/:token             — Patient portal entry (magic link)
 *   /f/:locationId/:slug  — Public form (open or OTP-gated)
 *   /pkg/:locationId/:slug — Public form package (OTP-gated, multi-form run)
 */
export default function App(): React.ReactElement {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* ── Staff app — full layout (GHL iframe, full-screen embeds) ── */}
          <Route
            path="/app/mailbox"
            element={
              <GHLAuthGate>
                <StaffLayout>
                  <MailboxPage />
                </StaffLayout>
              </GHLAuthGate>
            }
          />
          <Route
            path="/app/forms"
            element={
              <GHLAuthGate>
                <StaffLayout>
                  <FormsPage />
                </StaffLayout>
              </GHLAuthGate>
            }
          />
          <Route
            path="/app/settings"
            element={
              <GHLAuthGate>
                <StaffLayout>
                  <RequireAdmin>
                    <SettingsPage />
                  </RequireAdmin>
                </StaffLayout>
              </GHLAuthGate>
            }
          />

          {/* ── Staff contact widget — compact embed (no StaffLayout) ── */}
          <Route
            path="/app/contact/:contactId"
            element={
              <GHLAuthGate>
                <ContactWidgetPage />
              </GHLAuthGate>
            }
          />

          {/* ── Patient portal — magic link entry ── */}
          <Route path="/c/:token" element={<ContactPortalPage />} />

          {/* ── Public form ── */}
          <Route path="/f/:locationId/:slug" element={<PublicFormPage />} />

          {/* ── Public form package ── */}
          <Route path="/pkg/:locationId/:slug" element={<PublicPackagePage />} />

          {/* ── Default fallback ── */}
          <Route path="*" element={<Navigate to="/app/mailbox" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
